import type { ReactNode, CSSProperties, ElementType } from 'react';

type HeadingLevel = 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
type HeadingSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl' | '4xl';

interface HeadingProps {
  children: ReactNode;
  /**
   * Semantic heading level
   * @default 'h2'
   */
  level?: HeadingLevel;
  /**
   * Visual size (can differ from semantic level)
   */
  size?: HeadingSize;
  /**
   * Additional CSS class
   */
  className?: string;
  /**
   * Test ID for testing
   */
  'data-testid'?: string;
}

const sizeMap: Record<HeadingSize, string> = {
  xs: '0.75rem',
  sm: '0.875rem',
  md: '1rem',
  lg: '1.25rem',
  xl: '1.5rem',
  '2xl': '2rem',
  '3xl': '2.5rem',
  '4xl': '3rem',
};

const defaultSizes: Record<HeadingLevel, HeadingSize> = {
  h1: '3xl',
  h2: '2xl',
  h3: 'xl',
  h4: 'lg',
  h5: 'md',
  h6: 'sm',
};

/**
 * Heading component for semantic headings with flexible sizing.
 *
 * @example
 * <Heading level="h1" size="2xl">Page Title</Heading>
 * <Heading level="h2">Section Title</Heading>
 * <Heading level="h3" size="lg">Subsection</Heading>
 */
export function Heading({
  children,
  level = 'h2',
  size,
  className = '',
  'data-testid': testId,
}: HeadingProps) {
  const Component = level as ElementType;
  const actualSize = size || defaultSizes[level];

  const style: CSSProperties = {
    fontSize: sizeMap[actualSize],
    fontWeight: 600,
    lineHeight: 1.25,
    margin: 0,
  };

  return (
    <Component className={className} style={style} data-testid={testId}>
      {children}
    </Component>
  );
}
